import { Vector3, Curve3, Scene, MeshBuilder, Color3, Mesh } from '@babylonjs/core';
import { logger } from '../core/Logger';

export class SplineLoader {
  constructor(private scene: Scene) {}

  /**
   * Create a Catmull-Rom spline from an array of points
   */
  createFromPoints(points: Vector3[], closed: boolean = false): Curve3 {
    if (points.length < 2) {
      throw new Error('Spline requires at least 2 points');
    }

    const spline = Curve3.CreateCatmullRomSpline(points, points.length * 10, closed);
    return spline;
  }

  /**
   * Create a spline from a flat array of coordinates [x, y, z, x, y, z...]
   * @param data Flat array of coordinates
   * @param swapYZ Whether to swap Y and Z coordinates (useful for Blender -> Babylon conversion of raw data)
   */
  createFromRawData(data: number[], swapYZ: boolean = false): Curve3 {
    const points: Vector3[] = [];
    
    for (let i = 0; i < data.length; i += 3) {
      const x = data[i];
      const y = data[i + 1];
      const z = data[i + 2];
      
      if (swapYZ) {
        points.push(new Vector3(x, z, y));
      } else {
        points.push(new Vector3(x, y, z));
      }
    }
    
    return this.createFromPoints(points, false);
  }

  /**
   * Create a spline from a mesh's vertices (for GLTF curve meshes)
   * @param mesh The mesh to extract points from
   * @param useWorldCoordinates If true, transforms points to world space (useful if mesh is scaled/rotated)
   */
  createFromMesh(mesh: Mesh, useWorldCoordinates: boolean = false): Curve3 {
    const positions = mesh.getVerticesData('position');
    if (!positions) {
      throw new Error(`Mesh ${mesh.name} has no position data`);
    }

    const points: Vector3[] = [];
    const worldMatrix = useWorldCoordinates ? mesh.computeWorldMatrix(true) : null;

    for (let i = 0; i < positions.length; i += 3) {
      const point = new Vector3(positions[i], positions[i + 1], positions[i + 2]);
      
      if (useWorldCoordinates && worldMatrix) {
        points.push(Vector3.TransformCoordinates(point, worldMatrix));
      } else {
        points.push(point);
      }
    }

    return this.createFromPoints(points, false);
  }

  /**
   * Create a spline from a mesh strip (ribbon), averaging pairs of vertices to find the center line.
   * Useful for meshes created from curves with width/bevel.
   */
  createFromMeshStrip(mesh: Mesh, useWorldCoordinates: boolean = false): Curve3 {
    const positions = mesh.getVerticesData('position');
    if (!positions) {
      throw new Error(`Mesh ${mesh.name} has no position data`);
    }

    const points: Vector3[] = [];
    const worldMatrix = useWorldCoordinates ? mesh.computeWorldMatrix(true) : null;

    // We assume the mesh is a strip where vertices are generated in pairs (Left/Right)
    // e.g. v0, v1, v2, v3... where v0-v1 is a cross-section.
    // We iterate by 2 and average them.
    for (let i = 0; i < positions.length - 5; i += 6) {
      // Get two vertices (x,y,z) * 2
      const x1 = positions[i];
      const y1 = positions[i + 1];
      const z1 = positions[i + 2];

      const x2 = positions[i + 3];
      const y2 = positions[i + 4];
      const z2 = positions[i + 5];

      const p1 = new Vector3(x1, y1, z1);
      const p2 = new Vector3(x2, y2, z2);

      // Average them to get center
      const center = p1.add(p2).scale(0.5);

      if (useWorldCoordinates && worldMatrix) {
        points.push(Vector3.TransformCoordinates(center, worldMatrix));
      } else {
        points.push(center);
      }
    }

    // If we have enough points, create spline
    if (points.length < 2) {
        // Fallback to standard mesh loading if strip logic fails (e.g. not enough points)
        logger.spline.warn(`Mesh ${mesh.name} didn't look like a strip (points: ${points.length}). Falling back to raw vertices.`);
        return this.createFromMesh(mesh, useWorldCoordinates);
    }

    return this.createFromPoints(points, false);
  }

  /**
   * Get the closest point on a spline to a target position
   */
  getClosestPoint(spline: Curve3, targetPosition: Vector3): { point: Vector3; t: number } {
    const points = spline.getPoints();
    let closestDistance = Infinity;
    let closestPoint = points[0];
    let closestIndex = 0;

    // Find closest point in the spline
    for (let i = 0; i < points.length; i++) {
      const distance = Vector3.Distance(points[i], targetPosition);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPoint = points[i];
        closestIndex = i;
      }
    }

    // Calculate t-value (0-1 normalized position along spline)
    const t = closestIndex / (points.length - 1);

    return { point: closestPoint, t };
  }

  /**
   * Project a position onto a spline (for 2.5D constraint)
   * Returns the projected position with the target's X but the spline's Z
   */
  projectToSpline(spline: Curve3, position: Vector3): { position: Vector3; t: number } {
    const closest = this.getClosestPoint(spline, position);
    
    // Keep the character's X position, but use the spline's Y and Z
    const projectedPosition = new Vector3(
      position.x,
      closest.point.y,
      closest.point.z
    );

    return { position: projectedPosition, t: closest.t };
  }

  /**
   * Get a point on the spline at a specific t-value (0-1)
   */
  getPointAtT(spline: Curve3, t: number): Vector3 {
    const points = spline.getPoints();
    const index = Math.floor(t * (points.length - 1));
    const clampedIndex = Math.max(0, Math.min(points.length - 1, index));
    return points[clampedIndex];
  }

  /**
   * Get the tangent (direction) at a specific point on the spline
   */
  getTangentAtT(spline: Curve3, t: number): Vector3 {
    const points = spline.getPoints();
    const index = Math.floor(t * (points.length - 1));
    const clampedIndex = Math.max(0, Math.min(points.length - 2, index));
    
    // Calculate tangent from two adjacent points
    const p1 = points[clampedIndex];
    const p2 = points[clampedIndex + 1];
    return p2.subtract(p1).normalize();
  }

  /**
   * Visualize a spline for debugging
   */
  visualizeSpline(spline: Curve3, name: string, color: Color3 = new Color3(1, 0, 1)): Mesh {
    const lines = MeshBuilder.CreateLines(
      name,
      { points: spline.getPoints() },
      this.scene
    );
    lines.color = color;
    // Mark as debug mesh to exclude from shadows
    lines.isPickable = false;
    lines.checkCollisions = false;
    lines.receiveShadows = false;
    lines.layerMask = 0x20000000; // Debug layer - filterMesh excludes from shadows
    lines.metadata = { isDebug: true, noShadows: true };
    
    return lines;
  }

  /**
   * Create a simple gameplay spline for testing (curved path on floor plane)
   */
  createTestGameplaySpline(): Curve3 {
    const points = [
      new Vector3(-10, 1, -3),
      new Vector3(-5, 1, 2),
      new Vector3(0, 1, 3),
      new Vector3(5, 1, 2),
      new Vector3(10, 1, -3)
    ];
    return this.createFromPoints(points);
  }

  /**
   * Create a simple camera spline for testing
   */
  createTestCameraSpline(): Curve3 {
    const points = [
      new Vector3(-10, 3, 5),
      new Vector3(-5, 4, 5),
      new Vector3(0, 4.5, 5),
      new Vector3(5, 4, 5),
      new Vector3(10, 3, 5)
    ];
    return this.createFromPoints(points);
  }

  /**
   * Create a circular spline
   * @param center Center of the circle
   * @param radius Radius of the circle
   * @param numPoints Number of points to generate
   */
  createCircleSpline(center: Vector3, radius: number, numPoints: number = 20): Curve3 {
    const points: Vector3[] = [];
    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * Math.PI * 2;
      const x = center.x + Math.cos(angle) * radius;
      const z = center.z + Math.sin(angle) * radius;
      points.push(new Vector3(x, center.y, z));
    }
    // Close the loop by adding the first point at the end
    points.push(points[0]);
    
    return this.createFromPoints(points, true);
  }
}
